import { useToast } from "@chakra-ui/react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type LibraryElementContentDto,
  type LibraryElementDto,
  type LibraryElementTypeDto,
  useCreateLibraryElementMutation,
  useListLibraryElementsQuery,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import { useApiError } from "../errors/useApiError";
import { FormOptionRow } from "./fields/FormSectionBand";

// t('Save to the requirement library') t('Becomes available in later procurements')
// t('Saved to the requirement library')

// Every library element is titled — the forms require it, because the title is what search matches on.
const labelOf = (e: LibraryElementDto): string =>
  e.content.minimum?.title ??
  e.content.selection?.name ??
  e.content.award?.name ??
  e.content.awardSubCriterion?.name ??
  "";

/**
 * "Keep this for later" as one behaviour, wherever a requirement is written.
 *
 * Growing the library is the admin's choice and a separate write from the one that puts the requirement
 * where the user actually wanted it — so it trails that write rather than gating it, and only its own
 * failure is reported. A library that refuses the copy must never cost the requirement just authored.
 *
 * The option is returned as a node rather than rendered here, so the drawers that show it can stay
 * presentational: the surface that owns the submit owns this hook, and hands the row down.
 */
export const useSaveToLibraryOption = (
  type: LibraryElementTypeDto,
) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { trigger } = useApiError();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const canWriteToLibrary = selectedOrg.canEditDocumentTemplates && selectedOrg.subscription?.hasBuyerAccess !== false;
  const [isChecked, setIsChecked] = useState(false);
  // No isLoading: by the time the write starts the form is gone, so there is nothing left to spin. A
  // failure reaches the user through the toast, which outlives it.
  const [createLibraryElement] = useCreateLibraryElementMutation();
  const { data } = useListLibraryElementsQuery({}, { skip: !canWriteToLibrary });

  const namesOfType = useMemo(() => {
    const names = new Set<string>();
    (data?.elements ?? []).forEach((element) => {
      const name = labelOf(element).trim().toLowerCase();
      // An element with no name is not a name every unnamed row matches — it would disable the action
      // on each of them, and none could ever be kept.
      if (element.content.type === type && name !== "") names.add(name);
    });
    return names;
  }, [data?.elements, type]);

  /** Whether the library already holds a requirement of this type under that name. */
  const isInLibrary = useCallback(
    (name: string | null | undefined) => {
      const key = (name ?? "").trim().toLowerCase();
      return key !== "" && namesOfType.has(key);
    },
    [namesOfType]
  );

  const write = useCallback(
    async (content: LibraryElementContentDto, announce: boolean) => {
      try {
        // A requirement written inside a procurement can only ever become an org element; a global
        // SourceMagnet standard is authored on the library page itself.
        await createLibraryElement({ createLibraryElementRequest: { content, global: false } }).unwrap();
        if (announce) {
          toast({ description: t("Saved to the requirement library"), status: "success", duration: 3000 });
        }
      } catch (e) {
        trigger(e);
      }
    },
    [createLibraryElement, toast, trigger, t]
  );

  /** The submit path: writes only if the option was left checked, and says nothing when it succeeds. */
  const saveIfChosen = useCallback(
    async (content: LibraryElementContentDto) => {
      if (!canWriteToLibrary || !isChecked) return;
      await write(content, false);
    },
    [canWriteToLibrary, isChecked, write]
  );

  /**
   * The deliberate path: a button pressed on a requirement that already exists. Nothing else on screen
   * changes when it lands, so this one confirms itself.
   */
  const save = useCallback(
    async (content: LibraryElementContentDto) => {
      if (!canWriteToLibrary) return;
      await write(content, true);
    },
    [canWriteToLibrary, write]
  );

  /** A fresh form starts from the default again — the last visit's answer is not this visit's intent. */
  const reset = useCallback(() => setIsChecked(false), []);

  const option = canWriteToLibrary ? (
    <FormOptionRow
      isChecked={isChecked}
      onChange={setIsChecked}
      title={t("Save to the requirement library")}
      hint={t("Becomes available in later procurements")}
    />
  ) : null;

  return { canWriteToLibrary, option, isChecked, reset, saveIfChosen, save, isInLibrary };
};
